import React from 'react'
import type { IOccurrencesAssociatedWeapon, ResponseProps } from '../interfaces'
import { createState, useState, type State } from '@hookstate/core'
import {
  getOccurrencesAssociatedWeaponByType,
  getOccurrencesAssociatedWeaponByCircunstance,
  getOccurrencesAssociatedWeaponByDestination,
  getOccurrencesAssociatedWeaponByOccurrenceType,
  getOccurrencesAssociatedWeaponByKindType,
} from '../components/QualifiersCharts/Weapon/services'

interface IOccurrencesAssociatedWeaponProps {
  readonly occurrencesAssociatedWeapon: IOccurrencesAssociatedWeapon
  readonly loadingCharts: boolean
}

const occurrencesAssociatedWeaponProps: Readonly<ResponseProps> = {
  item: null,
  value: null,
  total: null,
}

const occurrencesAssociatedWeaponDefault: Readonly<IOccurrencesAssociatedWeapon> =
  {
    occurrencesAssociatedWeaponByType: occurrencesAssociatedWeaponProps,
    occurrencesAssociatedWeaponByCircunstance: occurrencesAssociatedWeaponProps,
    occurrencesAssociatedWeaponByDestination: occurrencesAssociatedWeaponProps,
    occurrencesAssociatedWeaponByOccurrenceType:
      occurrencesAssociatedWeaponProps,
    occurrencesAssociatedWeaponByKindType: occurrencesAssociatedWeaponProps,
  }

const store = createState<Readonly<IOccurrencesAssociatedWeaponProps>>({
  occurrencesAssociatedWeapon: occurrencesAssociatedWeaponDefault,
  loadingCharts: true,
})

interface getAllsAssociatedWeaponDataProps {
  readonly startDate: string | null
  readonly finishDate: string | null
  readonly weaponTypeId: number | null
  readonly destinationId: number | null
  readonly circunstanceId: number | null
  readonly weaponKindId: number | null
}

export default function useManagementQualifiersWeaponState() {
  const occurrencesAssociatedWeapon: State<
    Readonly<IOccurrencesAssociatedWeapon>
  > = useState(store.occurrencesAssociatedWeapon)
  const loadingCharts = useState(store.loadingCharts)

  async function getAssociatedWeaponStats({
    startDate,
    finishDate,
    weaponTypeId,
    weaponKindId,
    destinationId,
    circunstanceId,
  }: getAllsAssociatedWeaponDataProps) {
    const params = {
      startDate,
      finishDate,
      weaponTypeId,
      destinationId,
      weaponKindId,
      circumstanceId: circunstanceId,
    }

    loadingCharts.set(true)

    const mapResponse = (response) =>
      response.map(({ item, value, total }) => ({ label: item, value, total }))

    Promise.all([
      getOccurrencesAssociatedWeaponByType(params).then((response) => {
        occurrencesAssociatedWeapon.occurrencesAssociatedWeaponByType.set(
          mapResponse(response)
        )
      }),
      getOccurrencesAssociatedWeaponByCircunstance(params).then((response) => {
        occurrencesAssociatedWeapon.occurrencesAssociatedWeaponByCircunstance.set(
          response
        )
      }),
      getOccurrencesAssociatedWeaponByDestination(params).then((response) => {
        occurrencesAssociatedWeapon.occurrencesAssociatedWeaponByDestination.set(
          response
        )
      }),
      getOccurrencesAssociatedWeaponByOccurrenceType(params).then(
        (response) => {
          occurrencesAssociatedWeapon.occurrencesAssociatedWeaponByOccurrenceType.set(
            response
          )
        }
      ),
      getOccurrencesAssociatedWeaponByKindType(params).then((response) => {
        occurrencesAssociatedWeapon.occurrencesAssociatedWeaponByKindType.set(
            response
          )
      }),
    ]).finally(() => {
      loadingCharts.set(false)
    })
  }

  return {
    occurrencesAssociatedWeapon: () => occurrencesAssociatedWeapon,
    fetchAllAssociatedWeaponData: async ({
      destinationId,
      finishDate,
      weaponTypeId,
      circunstanceId,
      startDate,
      weaponKindId,
    }) =>
      getAssociatedWeaponStats({
        destinationId,
        finishDate,
        weaponTypeId,
        circunstanceId,
        startDate,
        weaponKindId,
      }),
    loadingCharts: loadingCharts,
  }
}
